import { useCallback, useState } from 'react';

/**
 * Custom hook to manage a boolean state with a toggle function.
 * This hook uses the `useState` hook to manage the state and the `useCallback` hook to memoize the toggle function.
 *
 * @param {boolean} [initialState=false] - The initial state of the boolean value.
 * @returns {[boolean, () => void]} - Returns an array with the current state and a function to toggle the state.
 *
 * @example
 * const [isOpen, toggleOpen] = useToggle();
 * toggleOpen(); // Toggles the state to true
 */
export function useToggle(initialState = false): [boolean, () => void] {
  // useState hook to manage the boolean state
  const [open, setOpen] = useState<boolean>(initialState);

  // useCallback hook to memoize the toggle function
  const toggle = useCallback(() => {
    // Toggle the state by setting it to the opposite of the previous state
    setOpen(prev => !prev);
  }, [setOpen]);

  // Return the current state and the toggle function
  return [open, toggle];
}
